import React, { useEffect, useRef, useState } from 'react';

function formatTime(seconds) {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${String(secs).padStart(2, '0')}`;
}

export default function GraduateVideoPlayer({ src, name, role }) {
  const videoRef = useRef(null);
  const hideTimerRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [showUi, setShowUi] = useState(true);

  const revealUi = (keepVisible = false) => {
    setShowUi(true);
    window.clearTimeout(hideTimerRef.current);
    if (keepVisible) return;
    const video = videoRef.current;
    if (video && !video.paused) {
      hideTimerRef.current = window.setTimeout(() => setShowUi(false), 2000);
    }
  };

  useEffect(() => () => window.clearTimeout(hideTimerRef.current), []);

  const pauseOthers = () => {
    const current = videoRef.current;
    document.querySelectorAll('.story-player video').forEach((video) => {
      if (video !== current) video.pause();
    });
  };

  const togglePlay = (event) => {
    event?.stopPropagation();
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      pauseOthers();
      video.play();
    } else {
      video.pause();
    }
  };

  const seek = (event) => {
    event.stopPropagation();
    const video = videoRef.current;
    const bar = event.currentTarget.getBoundingClientRect();
    if (!video || !video.duration || !bar.width) return;
    const ratio = Math.min(1, Math.max(0, (event.clientX - bar.left) / bar.width));
    video.currentTime = ratio * video.duration;
  };

  const toggleMute = (event) => {
    event.stopPropagation();
    const video = videoRef.current;
    if (!video) return;
    video.muted = !video.muted;
    setIsMuted(video.muted);
  };

  return (
    <div
      className={`story-player${isPlaying ? ' is-playing' : ''}${showUi ? ' is-ui-visible' : ''}`}
      onMouseMove={() => revealUi()}
      onPointerDown={(event) => event.stopPropagation()}
    >
      <div className="story-player-stage">
        <video
          ref={videoRef}
          key={src}
          className="testimonial-video"
          src={src}
          playsInline
          preload="metadata"
          onLoadedMetadata={(event) => {
            const video = event.currentTarget;
            setDuration(video.duration || 0);
          }}
          onPlay={() => {
            setIsPlaying(true);
            revealUi();
          }}
          onPause={() => {
            setIsPlaying(false);
            revealUi(true);
          }}
          onEnded={() => {
            setIsPlaying(false);
            setProgress(0);
            setCurrentTime(0);
            revealUi(true);
          }}
          onTimeUpdate={() => {
            const video = videoRef.current;
            if (!video || !video.duration) return;
            setCurrentTime(video.currentTime);
            setProgress(video.currentTime / video.duration);
          }}
        >
          הדפדפן שלך לא תומך בוידאו.
        </video>

        <button
          type="button"
          className="story-player-hit"
          onClick={togglePlay}
          aria-label={isPlaying ? `השהה את הווידאו של ${name}` : `נגן את הווידאו של ${name}`}
        />

        <div className="story-player-overlay">
          {!isPlaying && (
            <button
              type="button"
              className="story-play-btn"
              onClick={togglePlay}
              tabIndex={-1}
              aria-hidden="true"
            >
              <span className="story-play-pulse" />
              <svg className="story-play-icon" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M8.2 5.6v12.8c0 .7.8 1.1 1.4.7l10-6.4c.6-.4.6-1.2 0-1.6l-10-6.4c-.6-.4-1.4 0-1.4.9z" />
              </svg>
            </button>
          )}

          <div className="story-caption">
            <h4 className="story-caption-name">{name}</h4>
            <p className="story-caption-role">{role}</p>
          </div>

          <div className="story-dock">
            <button
              type="button"
              className="story-progress"
              dir="ltr"
              onClick={seek}
              aria-label="התקדמות הווידאו"
            >
              <span className="story-progress-fill" style={{ width: `${progress * 100}%` }} />
            </button>
            <div className="story-dock-row">
              <button
                type="button"
                className="story-mute-btn"
                onClick={toggleMute}
                aria-label={isMuted ? 'הפעל קול' : 'השתק'}
              >
                {isMuted ? (
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M5 10v4h3l4 3V7L8 10H5zm11.5 2 2.2 2.2-.7.7L15.8 12.7l-2.2 2.2-.7-.7 2.2-2.2-2.2-2.2.7-.7 2.2 2.2 2.2-2.2.7.7L16.5 12z" />
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M5 10v4h3l4 3V7L8 10H5zm11.5 2a3.5 3.5 0 0 0-1.8-3.1v6.2a3.5 3.5 0 0 0 1.8-3.1zm-1.8-7v1.5a6 6 0 0 1 0 11v1.5a7.5 7.5 0 0 0 0-14z" />
                  </svg>
                )}
              </button>
              <span className="story-player-time" dir="ltr">
                {formatTime(currentTime)}
                <span className="story-player-time-sep"> / </span>
                {formatTime(duration)}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
